/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { requestBaseURL } from '@/config/serviceAPI';
import { getAxios } from '@/lib/axios';
import { type AxiosInstance } from 'axios';
import { useMutation } from '@tanstack/react-query';
import type {
  AddToCartData,
  AddToCartResponse,
  AddToCartPayload,
} from '@/types/reduxStore/cartUpdate';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { useAcesDealsVehicleAttributes } from '@/features/deals/hooks/useAcesDealsVehicleAttributes';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import {
  useACESVehicles,
  getPreferredVehicleVinMileage,
  type PreferredVehicleVinMileage,
} from '@/features/ymme';

const URL = `${requestBaseURL}/ecomm/b2c/order/v4/item`;
const ACES_URL = `${requestBaseURL}/ecomm/b2c/order/v5/item`;

const addToCart = async (options: AddToCartPayload, axiosInstance?: AxiosInstance) => {
  const {
    skuId,
    productId,
    quantity,
    fulfillmentTypeId,
    vehicleId,
    storeNumber,
    searchedTerm,
    configData,
    xFusionQueryId,
    vehicleAttributes,
    acesEnabled,
    positionId,
    brandId,
    vin,
    mileage,
    uniqueId,
  } = options;

  const response = await getAxios(axiosInstance).post<AddToCartResponse>(
    acesEnabled ? ACES_URL : URL,
    {
      skuId,
      productId,
      quantity,
      fulfillmentTypeId,
      vehicleId,
      storeNumber,
      configData,
      searchedTerm,
      xFusionQueryId,
      ...(acesEnabled && vehicleAttributes && { vehicleAttributes }),
      ...(acesEnabled && positionId && { positionId }),
      ...(acesEnabled && brandId && { brandId }),
      ...(acesEnabled && uniqueId && { uuid: uniqueId }),
      ...(vin && { vin }),
      ...(mileage && { mileage }),
    },
  );

  return selector(response.data);
};

const selector = (data: AddToCartResponse): AddToCartData => data;

export const useAddToCart = ({ onAddToCartSuccess }: { onAddToCartSuccess?: () => void } = {}) => {
  const preferredVehicle = usePreferredVehicle();
  const acesVehicleAttributes = useAcesDealsVehicleAttributes({
    engine: preferredVehicle?.engine,
    makeName: preferredVehicle?.make,
    modelName: preferredVehicle?.model,
    regionName: preferredVehicle?.region,
    subModelName: preferredVehicle?.subModel,
    vehicleTypeName: preferredVehicle?.vehicleTypeName,
  });
  const { acesEnabled } = useAcesFlags();

  const { data: acesVehiclesData } = useACESVehicles({ enabled: true });
  const vehicleDetails: PreferredVehicleVinMileage =
    getPreferredVehicleVinMileage(acesVehiclesData);

  return useMutation({
    mutationFn: ({
      skuId,
      productId,
      quantity,
      fulfillmentTypeId,
      vehicleId,
      storeNumber,
      configData,
      searchedTerm,
      xFusionQueryId,
      positionId,
      brandId,
      uniqueId,
    }: Omit<AddToCartPayload, 'vehicleAttributes' | 'acesEnabled' | 'vin' | 'mileage'>) => {
      const payload: AddToCartPayload = {
        skuId,
        productId,
        quantity,
        fulfillmentTypeId,
        vehicleId,
        storeNumber,
        configData,
        searchedTerm,
        xFusionQueryId,
        acesEnabled,
        ...(positionId && { positionId }),
        ...(brandId && { brandId }),
        ...(uniqueId && { uniqueId }),
        ...(acesVehicleAttributes && {
          vehicleAttributes: acesVehicleAttributes,
        }),
        ...(vehicleDetails.vin && { vin: vehicleDetails.vin }),
        ...(vehicleDetails.mileage && { mileage: vehicleDetails.mileage }),
      };

      return addToCart(payload);
    },
    onSuccess: () => {
      onAddToCartSuccess?.();
    },
  });
};
